// @ts-check
import React from "react";
import ReactDOM from 'react-dom';
import { useDropdown } from '../../hooks/useDropdown';
import { Manager, Reference, Popper } from 'react-popper';
import { Link } from 'react-router';
import DotsHorizontalIcon from '@heroicons/react/outline/DotsHorizontalIcon'
import { FAIcon } from "../FAIcon";

/** @typedef { "blue" | "red" } DropdownItemColor */

/** @type { Record<DropdownItemColor, string> } */
const colorClassNames = {
  blue: 'text-gray-800 hover:bg-blue-200 hover:text-blue-800',
  red: 'text-red-800 hover:bg-red-200 hover:text-red-600',
};

/**
 * DropdownButton.
 */

/** @typedef {{
  label?: React.ReactNode
}} DropdownButtonProps */

/** @type { React.FC<DropdownButtonProps> } */
export const DropdownButton = props => {
  const { label, children } = props;
  const { isOpen, toggle, triggerRef, contentRef } = useDropdown();

  return (
    <Manager>
      <Reference>
        {({ ref }) => (
          <div ref={ref} className="inline-block">
            <button
              ref={triggerRef}
              onClick={toggle}
              type="button"
              className="hover:bg-gray-100 focus:outline-none focus:shadow-outline h-8 px-1 ml-2 font-medium text-gray-800 border border-gray-300 rounded"
            >
              {!label ? (
                <DotsHorizontalIcon className="w-6 h-6" />
              ) : (
                <p className="flex items-center px-1 text-sm">
                  {label}
                  <span className="mt-1 ml-2 text-xs text-gray-700">
                    <FAIcon icon="chevron-down" />
                  </span>
                </p>
              )}
            </button>
          </div>
        )}
      </Reference>
      {isOpen &&
        ReactDOM.createPortal(
          <Popper placement="bottom-end">
            {({ placement, ref, style }) => (
              <div ref={ref} style={style} data-placement={placement} className="z-40">
                <div
                  className="absolute right-0 z-10 mt-1 origin-top-right"
                  ref={contentRef}
                  hidden={!isOpen}
                >
                  <div className="w-48 px-1 pb-1 text-left bg-white border border-gray-300 rounded-lg shadow-lg">
                    {children}
                  </div>
                </div>
              </div>
            )}
          </Popper>,
          document.querySelector('#dropdown-button-action-portal')
        )}
    </Manager>
  );
};

/**
 * DropdownItemButton.
 */

/** @typedef {{
  onClick?(): void;
  className?: string;
  color?: DropdownItemColor;
}} DropdownItemButtonProps */

/** @type { React.FC<DropdownItemButtonProps> } */
export const DropdownItemButton = props => {
  const { className, color = 'blue', onClick, children } = props;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`block w-full bg-white text-sm font-semibold text-left cursor-pointer rounded mt-1 px-2 py-1 ${className ||
        ''} ${colorClassNames[color]}`}
    >
      {children}
    </button>
  );
};

/**
 * DropdownItemLink.
 */

/** @typedef {{
  to: string;
  className?: string;
  color?: DropdownItemColor;
}} DropdownItemLinkProps */

/** @type { React.FC<DropdownItemLinkProps> } */
export const DropdownItemLink = props => {
  const { className, to, color = 'blue', children } = props;

  return (
    <Link
      to={to}
      className={`block w-full bg-white text-sm font-semibold text-left cursor-pointer rounded mt-1 px-2 py-1 ${className ||
        ''} ${colorClassNames[color]}`}
    >
      {children}
    </Link>
  );
};
